import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { MONTH_NAMES } from './dreUtils';
import type { DREMonthlyReport, DREMonthlySection } from './types';

interface Props {
  report: DREMonthlyReport;
}

/** Número compacto para células mensais: sem símbolo, sem centavos, "–" para zero. */
function fmtNum(value: number): string {
  if (value === 0) return '–';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function fmtPct(value: number | null): string {
  if (value === null) return '–';
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function monthAbbr(month: number): string {
  return MONTH_NAMES[month - 1]!.slice(0, 3);
}

function SectionBlock({
  section,
  months,
  title,
  isExpense,
}: {
  section: DREMonthlySection;
  months: number[];
  title: string;
  isExpense: boolean;
}) {
  const accent = isExpense ? 'text-expense' : 'text-income';
  const totalBg = isExpense ? 'bg-expense/5' : 'bg-income/5';
  return (
    <>
      <tr className={isExpense ? 'bg-expense/10' : 'bg-income/10'}>
        <td className={cn('px-3 py-2 text-xs font-bold uppercase tracking-wide', accent)}>
          {title}
        </td>
        <td colSpan={months.length + 2} />
      </tr>

      {section.lines.length === 0 ? (
        <tr>
          <td
            colSpan={months.length + 3}
            className="px-3 py-4 text-center text-sm text-muted-foreground"
          >
            Sem lançamentos no período.
          </td>
        </tr>
      ) : (
        section.lines.map((line) => (
          <tr key={line.categoryId ?? '__sem__'} className="border-b border-border/40">
            <td className="whitespace-nowrap px-3 py-2">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: line.color }}
                />
                <span className="text-sm text-foreground">{line.categoryName}</span>
              </div>
            </td>
            {line.monthly.map((v, i) => (
              <td
                key={months[i]}
                className="px-3 py-2 text-right text-sm tabular-nums text-foreground"
              >
                {fmtNum(v)}
              </td>
            ))}
            <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums text-foreground">
              {fmtNum(line.total)}
            </td>
            <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
              {fmtPct(line.verticalPct)}
            </td>
          </tr>
        ))
      )}

      <tr className={cn('border-b-2 border-border font-semibold', totalBg)}>
        <td className={cn('whitespace-nowrap px-3 py-2 text-sm', accent)}>Total {title}</td>
        {section.monthlyTotals.map((v, i) => (
          <td key={months[i]} className={cn('px-3 py-2 text-right text-sm tabular-nums', accent)}>
            {fmtNum(v)}
          </td>
        ))}
        <td className={cn('px-3 py-2 text-right text-sm tabular-nums', accent)}>
          {fmtNum(section.total)}
        </td>
        <td className={cn('px-3 py-2 text-right text-xs tabular-nums', accent)}>
          {fmtPct(section.verticalPct)}
        </td>
      </tr>
    </>
  );
}

export function DRETable({ report }: Props) {
  const { months } = report;
  const netFavorable = report.netTotal >= 0;

  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-3 text-left">Categoria</th>
              {months.map((m) => (
                <th key={m} className="px-3 py-3 text-right">
                  {monthAbbr(m)}
                </th>
              ))}
              <th className="px-3 py-3 text-right">Total</th>
              <th className="px-3 py-3 text-right">%</th>
            </tr>
          </thead>

          <tbody>
            <SectionBlock
              section={report.income}
              months={months}
              title="Receitas"
              isExpense={false}
            />
            <SectionBlock
              section={report.expense}
              months={months}
              title="Despesas"
              isExpense={true}
            />

            <tr
              className={cn(
                'font-bold',
                netFavorable ? 'bg-income/15 text-income' : 'bg-expense/15 text-expense',
              )}
            >
              <td className="whitespace-nowrap px-3 py-3 text-sm uppercase tracking-wide">
                Resultado
              </td>
              {report.netByMonth.map((v, i) => (
                <td key={months[i]} className="px-3 py-3 text-right text-sm tabular-nums">
                  {fmtNum(v)}
                </td>
              ))}
              <td className="px-3 py-3 text-right text-base tabular-nums">
                {fmtNum(report.netTotal)}
              </td>
              <td className="px-3 py-3 text-right text-xs tabular-nums">
                {fmtPct(report.netVerticalPct)}
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
